<script lang="ts">
  import { untrack, type Component } from 'svelte';

  import AcceptInvitePage from './components/AcceptInvitePage.svelte';
  import AdminUsers from './components/AdminUsers.svelte';
  import AppHeader from './components/AppHeader.svelte';
  import CorpusBrowser from './components/CorpusBrowser.svelte';
  import DirectionWorkspace from './components/DirectionWorkspace.svelte';
  import HomePage from './components/HomePage.svelte';
  import InscriptionView from './components/InscriptionView.svelte';
  import LoginPage from './components/LoginPage.svelte';
  import SettingsPanel from './components/SettingsPanel.svelte';
  import TaskChooser from './components/TaskChooser.svelte';
  import TaskQueue from './components/TaskQueue.svelte';
  import Workspace from './components/Workspace.svelte';
  import { api } from './lib/api/client';
  import { router } from './lib/router.svelte';
  import { app } from './lib/stores/app.svelte';
  import { auth } from './lib/stores/auth.svelte';

  $effect(() => router.start());

  const route = $derived(router.route);

  /**
   * Routes an unauthenticated visitor may reach.
   *
   * `accept-invite` is on the list because the person following the link has no account yet
   * by construction — bouncing them to sign-in would be bouncing them to a form they cannot
   * complete.
   */
  const PUBLIC_ROUTES = ['login', 'accept-invite'] as const;
  const isPublicRoute = $derived(
    (PUBLIC_ROUTES as readonly string[]).includes(route.name),
  );

  /**
   * Re-read the account from the server on load.
   *
   * The cached user is only there for a flicker-free first paint. Authorization is the row's
   * `role`, read on every request server-side, so a demotion or a deactivation has to take
   * effect on the next page load rather than the next sign-in — and this is where that
   * happens. A 401 is handled inside the client and ends the session.
   */
  $effect(() => {
    untrack(() => {
      if (!auth.token || auth.checking) return;
      auth.checking = true;
      void api
        .me()
        .then((user) => auth.setUser(user))
        .catch(() => {
          /* `handleUnauthorized` has already cleared the session if that is what it was. */
        })
        .finally(() => {
          auth.checking = false;
        });
    });
  });

  // Untracked: `app.load()` writes the very fields it reads, and a tracked call would restart
  // itself every time one of them lands. Gated on being signed in — every endpoint it touches
  // needs a token, so firing it while signed out is a guaranteed 401.
  $effect(() => {
    if (!auth.signedIn) return;
    untrack(() => void app.load());
  });

  // Send an unauthenticated visitor to sign in, from wherever they landed. A deep link into
  // an image survives it in the sense that matters: they sign in and the app is there. It is
  // deliberately not restored automatically — see the note in `LoginPage.svelte`.
  $effect(() => {
    if (isPublicRoute || auth.signedIn || auth.checking) return;
    router.go({ name: 'login' });
  });

  /**
   * The dev-only box-style gallery (`#/styles`).
   *
   * Imported dynamically inside an `import.meta.env.DEV` branch so a production build has a
   * literal `if (false)` around the only reference to it and drops the chunk entirely — the
   * gallery pulls in its own sample data and prose, and none of that belongs in the bundle an
   * annotator loads.
   */
  let Gallery = $state<Component | null>(null);
  $effect(() => {
    if (route.name !== 'styles' || Gallery) return;
    if (import.meta.env.DEV) {
      void import('./components/StyleGallery.svelte').then((module) => {
        Gallery = module.default as unknown as Component;
      });
    }
  });

  /**
   * The guidelines page, split out of the main bundle.
   *
   * Same reasoning as the gallery above, for a different cost: it pulls in `marked`, and the
   * document it renders is something an annotator reads occasionally and a boxing session never
   * touches. Shipping a Markdown parser to everyone who opens the canvas to serve a page most
   * sessions never visit is the wrong trade. Unlike the gallery this one loads in production
   * too — it is a real page, not a dev tool.
   */
  let Guidelines = $state<Component | null>(null);
  $effect(() => {
    if (route.name !== 'guidelines' || Guidelines) return;
    void import('./components/GuidelinesPage.svelte').then((module) => {
      Guidelines = module.default as unknown as Component;
    });
  });
</script>

{#if route.name === 'login'}
  <!-- No header: it renders the account menu, and there is no account yet. -->
  <LoginPage />
{:else if route.name === 'accept-invite'}
  <AcceptInvitePage token={route.token} />
{:else if !auth.signedIn}
  <!-- The redirect above is already on its way; this is the frame in between. Rendering the
       app shell here would flash a work queue at somebody who cannot load it. -->
  <p class="muted" style="padding:16px">Checking your session…</p>
{:else}
  <div class="shell">
    <AppHeader />
    <main>
      {#if route.name === 'styles'}
        {#if Gallery}
          <Gallery />
        {:else}
          <p class="muted" style="padding:16px">
            The box-style gallery is a development-only page. Run <code>npm run dev</code> and
            open <code>#/styles</code> there.
          </p>
        {/if}
      {:else if route.name === 'admin'}
        {#if auth.isAdmin}
          <AdminUsers />
        {:else}
          <!-- The service answers 403 either way; saying so here means a non-admin who was
               sent the link reads an explanation rather than a failed table. -->
          <p class="muted" style="padding:16px">
            Account administration is limited to administrators.
          </p>
        {/if}
      {:else if route.name === 'settings'}
        <SettingsPanel />
      {:else if route.name === 'image'}
        {#key route.imageId}
          <Workspace imageId={route.imageId} />
        {/key}
      {:else if route.name === 'direction'}
        {#key route.imageId}
          <DirectionWorkspace imageId={route.imageId} />
        {/key}
      {:else if route.name === 'inscription'}
        {#key route.inscriptionId}
          <InscriptionView inscriptionId={route.inscriptionId} />
        {/key}
      {:else if route.name === 'queue'}
        <!-- No task chosen is the chooser, not a default task. Which job somebody sits down to
             do is a decision they make, and guessing it is how the queue ended up being two
             screens at once in the first place. -->
        {#if route.task}
          {#key route.task}
            <TaskQueue taskId={route.task} />
          {/key}
        {:else}
          <TaskChooser />
        {/if}
      {:else if route.name === 'browse'}
        <CorpusBrowser />
      {:else if route.name === 'guidelines'}
        {#if Guidelines}
          <Guidelines at={route.at} />
        {:else}
          <p class="muted" style="padding:16px">Loading the guidelines…</p>
        {/if}
      {:else}
        <!-- The fallback, and the landing page: an unknown hash lands on orientation, not on a
             work queue whose first question presumes you already know what a box is. -->
        <HomePage />
      {/if}
    </main>
  </div>
{/if}

<style>
  .shell {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
  }

  main {
    flex: 1;
    min-height: 0;
    overflow: auto;
  }
</style>
